import { Accordion, Typography, AccordionSummary, Stack, AccordionDetails } from '@mui/material';
import { netsuiteInvoiceActivitySelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import { FC } from 'react';
import { useToggle } from '../../../../../../../../../../../hooks/use-toggle';
import { isEmptyArray, isNullOrUndefined, ItemOf, Optional } from '@vy/utils';
import { formatPrice } from '@vy/services';
import { formatDate } from '../../../../../../../../../../../utils/date';
import { GetExportedIntegrationsResponse, GetLightInvoicesResponse } from '@vy/api-definition';
import { InvoiceDetailsEmptyState } from './invoice-details-accordion-components/InvoiceDetailsEmptyState';
import { SYNC_JOB_STATUSES } from '@vy/integrations-types';

type NetSuiteInvoiceDetailsAccordionProps = {
  netSuiteIntegration: Optional<ItemOf<GetExportedIntegrationsResponse>>;
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>;
};

export const NetSuiteInvoiceDetailsAccordion: FC<NetSuiteInvoiceDetailsAccordionProps> = ({
  netSuiteIntegration,
  invoice,
}) => {
  const [expanded, toggleExpanded] = useToggle(true);

  const invoiceDetails = getInvoiceDetails(netSuiteIntegration, invoice);

  return (
    <Accordion
      data-vy={netsuiteInvoiceActivitySelectors.invoiceDetailsAccordion}
      expanded={expanded}
      onChange={() => toggleExpanded()}
      sx={{ p: 0 }}
    >
      <AccordionSummary
        expandIcon={
          <VyIcon
            icon="ArrowDown"
            style={{
              color: '#323232',
            }}
          />
        }
        sx={{ p: 0, height: '34px', alignItems: 'center' }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1
          }}>
          <VyIcon icon="InvoiceList" />
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              lineHeight: "24px"
            }}>
            Invoice Details
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {isEmptyArray(invoiceDetails) ? (
          <InvoiceDetailsEmptyState />
        ) : (
          <Stack
            direction="column"
            style={{
              backgroundColor: '#F5F6FA',
              borderRadius: 1,
            }}
            sx={{
              gap: "24px",
              padding: "12px 20px",
              mb: 1.5,
              mt: 1.5,
              alignItems: "flex-start"
            }}>
            {invoiceDetails.map((detail) => (
              <Stack
                key={detail.label}
                direction="column"
                sx={{
                  alignItems: "flex-start",
                  gap: "2px"
                }}>
                <Typography
                  sx={{
                    lineHeight: "20px",
                    fontSize: 14
                  }}>
                  {detail.label}
                </Typography>
                <Typography
                  sx={{
                    lineHeight: "20px",
                    fontSize: 14,
                    color: "#7E8796"
                  }}>
                  {detail.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

function getInvoiceDetails(
  netSuiteIntegration: Optional<ItemOf<GetExportedIntegrationsResponse>>,
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>,
) {
  if (isNullOrUndefined(netSuiteIntegration) || isNetSuiteFailedSync(netSuiteIntegration)) {
    return [];
  }

  const EMPTY_DATA = '--';

  return [
    {
      label: 'Invoice amount',
      value: formatPrice(invoice.revenueBreakdown.total, invoice.currency),
    },
    {
      label: 'NetSuite Invoice ID',
      value: netSuiteIntegration.entityName ?? EMPTY_DATA,
    },
    {
      label: 'Posting date',
      value: isNullOrUndefined(invoice.issuedAt) ? EMPTY_DATA : formatDate(invoice.issuedAt),
    },
    {
      label: 'Due date',
      value: isNullOrUndefined(invoice.dueDate) ? EMPTY_DATA : formatDate(invoice.dueDate),
    },
    {
      label: 'Memo',
      value: EMPTY_DATA,
    },
  ];
}

function isNetSuiteFailedSync(netSuiteIntegration: Optional<ItemOf<GetExportedIntegrationsResponse>>) {
  return netSuiteIntegration?.status === SYNC_JOB_STATUSES.FAILED;
}
